import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Menu, X, User, LogOut, LayoutDashboard, Settings, ChevronDown, Bell, Shield } from 'lucide-react';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [logoClickCount, setLogoClickCount] = useState(0);
  const logoClickTimer = React.useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Secret admin access - 5 clicks on logo
  const handleLogoClick = (e) => {
    e.preventDefault();
    const newCount = logoClickCount + 1;
    setLogoClickCount(newCount);
    
    // Reset counter after 2 seconds of no clicks
    if (logoClickTimer.current) {
      clearTimeout(logoClickTimer.current);
    }
    logoClickTimer.current = setTimeout(() => {
      setLogoClickCount(0);
    }, 2000);
    
    // Navigate to admin after 5 clicks
    if (newCount >= 5) {
      setLogoClickCount(0);
      navigate('/admin');
    } else if (newCount === 1) {
      // Single click goes to home
      navigate('/');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;
  
  // Hide navbar on dashboard page (has its own sidebar)
  const hiddenPaths = ['/dashboard'];
  if (hiddenPaths.includes(location.pathname)) {
    return null;
  }

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled 
        ? 'bg-[hsl(220,20%,4%)]/95 backdrop-blur-xl border-b border-white/5 shadow-lg shadow-black/20' 
        : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 lg:h-28">
          {/* Logo - 5 clicks for secret admin access */}
          <div 
            onClick={handleLogoClick}
            className="flex items-center gap-3 cursor-pointer" 
            data-testid="nav-logo"
          >
            <img 
              src="/logo.png" 
              alt="Traders Capital" 
              className="h-20 lg:h-24 w-auto"
            />
          </div>

          {/* Affiliate Login Link - Top Left after Logo */}
          <Link 
            to="/affiliate-login"
            className="hidden lg:flex items-center px-4 py-2 ml-4 rounded-lg font-medium text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 transition-all border border-cyan-500/30"
            data-testid="nav-affiliates"
          >
            Affiliates
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            <Link 
              to="/get-funded" 
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                isActive('/get-funded') || isActive('/challenges') || isActive('/pricing')
                  ? 'text-blue-400 bg-blue-500/10' 
                  : 'text-gray-300 hover:text-white hover:bg-white/5'
              }`}
              data-testid="nav-get-funded"
            >
              Get Funded
            </Link>

            {/* Crypto Link */}
            <Link 
              to="/crypto" 
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                isActive('/crypto') 
                  ? 'text-yellow-400 bg-yellow-500/10' 
                  : 'text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10'
              }`}
              data-testid="nav-crypto"
            >
              KBTCOIN
            </Link>

            {/* Elite Funded Trader Link */}
            <Link 
              to="/elite-funded" 
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                isActive('/elite-funded') 
                  ? 'text-green-400 bg-green-500/10' 
                  : 'text-green-400 hover:text-green-300 hover:bg-green-500/10'
              }`}
              data-testid="nav-elite-funded"
            >
              Elite Funded
            </Link>
            
            {/* Trading Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="px-4 py-2 rounded-lg font-medium text-gray-300 hover:text-white hover:bg-white/5 transition-all flex items-center gap-1">
                  Trading Tools
                  <ChevronDown className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-48 bg-[hsl(220,20%,8%)] border border-white/10 shadow-xl">
                <DropdownMenuItem onClick={() => navigate('/analysis')} className="cursor-pointer text-gray-300 hover:text-white hover:bg-white/5 focus:bg-white/5 focus:text-white">
                  Technical Analysis
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/calendar')} className="cursor-pointer text-gray-300 hover:text-white hover:bg-white/5 focus:bg-white/5 focus:text-white">
                  Economic Calendar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link 
              to="/faq" 
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                isActive('/faq') 
                  ? 'text-blue-400 bg-blue-500/10' 
                  : 'text-gray-300 hover:text-white hover:bg-white/5'
              }`}
              data-testid="nav-faq"
            >
              FAQ
            </Link>
          </div>

          {/* Auth Section */}
          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="gap-2 border border-white/10 bg-white/5 text-white hover:bg-white/10 hover:border-white/20 rounded-full px-5"
                    data-testid="user-menu-trigger"
                  >
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
                      <User className="w-3 h-3 text-white" />
                    </div>
                    <span className="max-w-[100px] truncate font-medium">{user.full_name}</span>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52 bg-[hsl(220,20%,8%)] border border-white/10 shadow-xl">
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} className="cursor-pointer text-gray-300 hover:text-white hover:bg-white/5 focus:bg-white/5 focus:text-white" data-testid="menu-dashboard">
                    <LayoutDashboard className="w-4 h-4 mr-2 text-blue-400" />
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/settings/notifications')} className="cursor-pointer text-gray-300 hover:text-white hover:bg-white/5 focus:bg-white/5 focus:text-white" data-testid="menu-notifications">
                    <Bell className="w-4 h-4 mr-2 text-blue-400" />
                    Notifications
                  </DropdownMenuItem>
                  {user.is_admin && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer text-gray-300 hover:text-white hover:bg-white/5 focus:bg-white/5 focus:text-white" data-testid="menu-admin">
                      <Settings className="w-4 h-4 mr-2 text-blue-400" />
                      Admin Panel
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-400 hover:text-red-300 hover:bg-red-500/10 focus:bg-red-500/10 focus:text-red-300" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/login')} 
                  className="text-gray-300 hover:text-white hover:bg-white/5 font-medium rounded-full px-6"
                  data-testid="nav-login"
                >
                  Login
                </Button>
                <Button 
                  onClick={() => navigate('/register')} 
                  className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-bold rounded-full px-6 shadow-lg shadow-blue-500/25 transition-all hover:shadow-xl hover:shadow-blue-500/30"
                  data-testid="nav-register"
                >
                  GET FUNDED
                </Button>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-white/5 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6 text-white" /> : <Menu className="w-6 h-6 text-white" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-white/10 bg-[hsl(220,20%,4%)]/95 backdrop-blur-xl">
            <div className="flex flex-col gap-1">
              <Link 
                to="/get-funded" 
                className={`px-4 py-3 rounded-lg font-medium transition-all ${
                  isActive('/get-funded') || isActive('/challenges') || isActive('/pricing') ? 'text-blue-400 bg-blue-500/10' : 'text-gray-300 hover:bg-white/5'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Get Funded
              </Link>
              <Link 
                to="/crypto" 
                className={`px-4 py-3 rounded-lg font-medium transition-all ${
                  isActive('/crypto') ? 'text-yellow-400 bg-yellow-500/10' : 'text-yellow-400 hover:bg-yellow-500/10'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                KBTCOIN
              </Link>
              <Link 
                to="/elite-funded" 
                className={`px-4 py-3 rounded-lg font-medium transition-all ${
                  isActive('/elite-funded') ? 'text-green-400 bg-green-500/10' : 'text-green-400 hover:bg-green-500/10'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Elite Funded
              </Link>
              <Link 
                to="/analysis" 
                className={`px-4 py-3 rounded-lg font-medium transition-all ${
                  isActive('/analysis') ? 'text-blue-400 bg-blue-500/10' : 'text-gray-300 hover:bg-white/5'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Technical Analysis
              </Link>
              <Link 
                to="/calendar" 
                className={`px-4 py-3 rounded-lg font-medium transition-all ${
                  isActive('/calendar') ? 'text-blue-400 bg-blue-500/10' : 'text-gray-300 hover:bg-white/5'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Economic Calendar
              </Link>
              <Link 
                to="/faq" 
                className={`px-4 py-3 rounded-lg font-medium transition-all ${
                  isActive('/faq') ? 'text-blue-400 bg-blue-500/10' : 'text-gray-300 hover:bg-white/5'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                FAQ
              </Link>
              
              <div className="border-t border-white/10 mt-2 pt-2">
                {user ? (
                  <>
                    <Link 
                      to="/dashboard" 
                      className="px-4 py-3 rounded-lg font-medium text-gray-300 hover:bg-white/5 flex items-center gap-2 transition-all"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <LayoutDashboard className="w-4 h-4" />
                      Dashboard
                    </Link>
                    {user.is_admin && (
                      <Link 
                        to="/admin" 
                        className="px-4 py-3 rounded-lg font-medium text-gray-300 hover:bg-white/5 flex items-center gap-2 transition-all"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Settings className="w-4 h-4" />
                        Settings
                      </Link>
                    )}
                    <button 
                      onClick={handleLogout} 
                      className="w-full px-4 py-3 rounded-lg font-medium text-red-400 hover:bg-red-500/10 flex items-center gap-2 transition-all mt-2"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </>
                ) : (
                  <div className="flex gap-2 px-4 mt-2">
                    <Button 
                      variant="outline" 
                      onClick={() => { navigate('/login'); setMobileMenuOpen(false); }} 
                      className="flex-1 border border-white/10 text-white hover:bg-white/5 rounded-full"
                    >
                      Login
                    </Button>
                    <Button 
                      onClick={() => { navigate('/register'); setMobileMenuOpen(false); }} 
                      className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold rounded-full"
                    >
                      Get Started
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
